import { Component, OnInit } from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { RouterModule } from '@angular/router';
import { liability } from '../../../../functions/claim/liability';
import { ClaimKey } from '../../../../models/api/core/claim-key';
import { Reimbursement } from '../../../../models/api/view/claim/financials/reimbursements/reimbursement';
import { ReimbursementsSearchParameters } from '../../../../models/api/view/claim/financials/reimbursements/reimbursements-search-parameters';
import { alphaNumeric } from '../../../../validators/alpha-numeric';
import { HandleLoadingState } from '../../../../decorators/handle-loading-state.decorator';
import { BaseComponent } from '../../../base/base.component';
import { ButtonComponent } from '../../../core/buttons/button.component';
import { InputComponent } from '../../../core/controls/input.component';
import { RangeComponent } from '../../../core/controls/range.component';
import { PageHeaderComponent } from '../../../core/labels/page-header.component';
import { ProgressbarComponent } from '../../../core/labels/progressbar.component';
import { ActionRowComponent } from '../../../core/panels/action-row.component';
import { DetailItemComponent } from '../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../core/panels/detail-panel/detail-panel.component';
import { EmptyStateComponent } from '../../../core/panels/empty-state.component';
import { ColumnComponent } from '../../../core/table/column.component';
import { TableComponent } from '../../../core/table/table.component';

@Component({
  selector: 'app-reimbursements',
  imports: [
    TableComponent,
    ColumnComponent,
    RouterModule,
    PageHeaderComponent,
    InputComponent,
    RangeComponent,
    ActionRowComponent,
    ButtonComponent,
    ReactiveFormsModule,
    DetailPanelComponent,
    DetailItemComponent,
    EmptyStateComponent,
    ProgressbarComponent,
  ],
  template: `
    @if (workersComp || liability) {
      <app-page-header label="Reimbursements" />
      <form [formGroup]="fg">
        <div class="tw-flex tw-w-full">
          <div
            class="tw-flex tw-w-full tw-flex-col md:tw-flex-row tw-items-start"
          >
            <app-input
              class="tw-w-full md:tw-basis-1/3"
              [control]="fg.controls.docNum"
              label="Document number"
            />
            <app-range
              [control]="fg.controls.transactionDateStart"
              [toControl]="fg.controls.transactionDateEnd"
              class="tw-w-full md:tw-basis-2/3"
              [compact]="false"
              fromLabel="From date"
              toLabel="Through date"
            ></app-range>
          </div>
        </div>
        <div class="tw-flex tw-w-full tw-justify-center md:tw-justify-end">
          <app-action-row>
            <app-button
              right1
              type="submit"
              (click)="searchClick()"
              [loading]="loading"
              [disabled]="!hasInitialData || fg.invalid"
              label="Search"
            />
            <app-button
              right2
              emphasis="Low"
              (click)="clearClick()"
              label="Clear"
            />
          </app-action-row>
        </div>
      </form>
      <div class="tw-flex tw-flex-col md:tw-flex-row tw-w-full">
        @if (loading) {
          <app-empty-state
            height="300px"
            icon="fa-light fa-money-bill-transfer"
            label="Loading reimbursements"
            message="Retrieving reimbursement data"
            class="tw-w-full"
          >
            <div class="tw-py-4">
              <app-progressbar />
            </div>
          </app-empty-state>
        } @else if (error) {
          <app-empty-state
            icon="fa-light fa-triangle-exclamation"
            label="An error has occurred"
            class="tw-w-full tw-pt-6"
          />
        } @else if (isSearchResult && items.length === 0) {
          <app-empty-state
            icon="fa-filter fa-light"
            label="No match."
            message="Try a different filter criteria"
            class="tw-w-full tw-pt-6"
          />
        } @else {
          <div class="tw-flex tw-gap-4 tw-w-full">
            <div class="tw-w-full tw-overflow-x-auto">
              <app-table
                [dataSource]="items"
                [pageSize]="5"
                [scrollable]="true"
                dataKey="uniqueIdentifier"
                recordLabel="reimbursement"
                [expandable]="true"
                [sortField]="sortField"
                [sortOrder]="sortOrder"
              >
                <app-column name="carrierCode" label="Carrier code" />
                <app-column name="docNumber" label="Document number" />
                <app-column
                  name="receivedDate"
                  label="Received date"
                  format="d"
                />
                <app-column
                  name="enteredDate"
                  label="Entered date"
                  format="d"
                />
                <app-column name="bankNumber" label="Bank number" />
                <app-column name="type" label="Type" />
                <app-column name="amount" label="Amount" format="c" />
                <ng-template #expand let-data>
                  <div class="tw-flex tw-w-full tw-flex-col md:tw-flex-row">
                    <div class="tw-w-full md:tw-basis-1/2 tw-pr-0 md:tw-pr-3">
                      <app-detail-panel [dataSource]="data">
                        <app-detail-item
                          label="From date"
                          name="fromDate"
                          format="d"
                        />
                        <app-detail-item
                          label="Through date"
                          name="throughDate"
                          format="d"
                        />
                        <app-detail-item label="Login" name="login" />
                      </app-detail-panel>
                    </div>
                    <div class="tw-w-full md:tw-basis-1/2 tw-pr-0 md:tw-pr-3">
                      <app-detail-panel [dataSource]="data">
                        <app-detail-item label="Comments" name="comments" />
                        <app-detail-item
                          label="Comments detail"
                          name="commentsDetail"
                        />
                      </app-detail-panel>
                    </div>
                  </div>
                </ng-template>
              </app-table>
            </div>
          </div>
        }
      </div>
    }
    <div class="tw-flex">
      <div class="tw-basis-full tw-pr-4">&nbsp;</div>
    </div>
  `,
  styles: ``,
})
export class ReimbursementsComponent extends BaseComponent implements OnInit {
  loading = true;
  error: any = null;
  hasInitialData = false;
  workersComp = false;
  liability = false;
  claim: any = {};
  claimKey: ClaimKey;

  items: Reimbursement[] = [];
  sortField = 'carrierCode';
  sortOrder = 1; // asc
  isSearchResult = false;

  fg = new FormGroup({
    docNum: new FormControl<string>(null, [
      Validators.maxLength(10),
      alphaNumeric('Invalid format'),
    ]),
    transactionDateStart: new FormControl<Date>(null),
    transactionDateEnd: new FormControl<Date>(null),
  });

  @HandleLoadingState({ useViewLoading: true })
  async ngOnInit() {
    this.claim = this.view.currentClaim();
    this.workersComp = this.claim.lineOfBusiness === 'WC';
    this.liability = liability(this.claim.lineOfBusiness);

    this.claimKey = {
      recordId: this.claim.recordId,
      recordSource: this.claim.recordSource,
    };

    const response = await this.api.reimbursements.get(this.claimKey);
    this.items = response.reimbursements;
    this.hasInitialData = this.items?.length > 0;
  }

  /**
   * Search reimbursements with filters from the form.
   * Uses ReimbursementSearchParamsBuilder for input validation and sanitization.
   */
  @HandleLoadingState()
  protected async searchClick() {
    // Check Angular form validators first
    if (this.fg.invalid) {
      this.fg.markAllAsTouched();
      return;
    }

    const searchParams = this.buildSearchParams({
      docNum: this.fg.controls.docNum.value,
      transactionDateStart: this.fg.controls.transactionDateStart.value,
      transactionDateEnd: this.fg.controls.transactionDateEnd.value,
    });

    // Validation failed - invalid document number format (fallback check)
    if (searchParams === null) {
      this.fg.controls.docNum.setErrors({ alphaNumeric: 'Invalid format' });
      this.fg.controls.docNum.markAsTouched();
      return;
    }

    const response = await this.api.reimbursements.search(
      this.claimKey,
      searchParams,
    );
    this.isSearchResult = true;
    this.items = response.reimbursements;
  }

  /**
   * Clear filters and restore initial reimbursements from cache
   */
  @HandleLoadingState()
  async clearClick() {
    this.fg.reset();
    this.isSearchResult = false;
    // Re-fetch from HTTP cache (no network call - cached for 15 min)
    const response = await this.api.reimbursements.get(this.claimKey);
    this.items = response.reimbursements;
  }

  /**
   * Build search parameters from form values.
   * Validates and sanitizes input values.
   * @returns ReimbursementsSearchParameters or null if validation fails
   */
  private buildSearchParams(formValues: {
    docNum: string | null;
    transactionDateStart: Date | null;
    transactionDateEnd: Date | null;
  }): ReimbursementsSearchParameters | null {
    const searchParams: ReimbursementsSearchParameters = {};

    // Add optional document number parameter only if it has a value and is valid
    if (formValues.docNum && formValues.docNum.trim() !== '') {
      const sanitizedDocNum = this.sanitizeDocNumber(formValues.docNum);
      if (!this.isValidDocNumber(sanitizedDocNum)) {
        // Invalid document number format
        return null;
      }
      searchParams.docNumber = sanitizedDocNum;
    }

    // Add optional date parameters only if they have values
    if (formValues.transactionDateStart) {
      searchParams.fromDate = formValues.transactionDateStart;
    }
    if (formValues.transactionDateEnd) {
      searchParams.throughDate = formValues.transactionDateEnd;
    }

    return searchParams;
  }

  /**
   * Sanitize document number input.
   * Trims whitespace from the input.
   */
  private sanitizeDocNumber(docNum: string): string {
    return docNum.trim();
  }

  /**
   * Validate document number format.
   * Ensures document number doesn't contain suspicious characters that could be used for injection.
   * Must be 10 characters or less and contain only alphanumeric characters.
   */
  private isValidDocNumber(docNum: string): boolean {
    return docNum.length <= 10 && /^[a-zA-Z0-9]+$/.test(docNum);
  }
}
